import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { useState, useRef, useEffect, useImperativeHandle, useMemo, useCallback, } from "react";
import { MentionMenu } from "./MentionMenu";
import { FileDoc, FilePdf, FilePpt, FileXls } from "@phosphor-icons/react";
import { renderToStaticMarkup } from "react-dom/server";
const EMPTY_MENTION_DATA = { sections: [] };
const OVERVIEW_VISIBLE_ITEMS = 3;
const SECTION_VIEW_VISIBLE_ITEMS = 6;
const getSeeMoreEntryKey = (sectionId) => `action:see-more:${sectionId}`;
const getBackEntryKey = (sectionId) => `action:back:${sectionId}`;
const getItemEntryKey = (sectionId, itemId) => `item:${sectionId}:${itemId}`;
const getContextActionEntryKey = (sectionId, itemId) => `action:context:${sectionId}:${itemId}`;
const getUploadEntryKey = (sectionId) => `action:upload:${sectionId}`;
const getSelectionSeedForPlacement = (placement) => placement === "below" ? Number.MAX_SAFE_INTEGER : 0;
export const ChatInput = React.forwardRef(({ value, onChange, onKeyDown, onFocus, onBlur, placeholder = "What do you need help with?", disabled = false, fetchMentionItems, onUploadClick, uploadingFiles = [], sizeVariant = "default", }, ref) => {
    const [showMentionMenu, setShowMentionMenu] = useState(false);
    const [mentionSearchQuery, setMentionSearchQuery] = useState("");
    const [menuPosition, setMenuPosition] = useState({ top: 8, left: 0 });
    const [mentionMenuPlacement, setMentionMenuPlacement] = useState("above");
    const [mentionMenuMaxHeight, setMentionMenuMaxHeight] = useState(320);
    const [mentionSections, setMentionSections] = useState([]);
    const [mentionContextActions, setMentionContextActions] = useState([]);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [selectedMenuEntryIndex, setSelectedMenuEntryIndex] = useState(0);
    const editorRef = useRef(null);
    const isCompact = sizeVariant === "compact";
    const mentionTagFontSizePx = isCompact ? 10 : 12;
    const mentionTagIconSizePx = isCompact ? 10 : 12;
    const mentionTagBaselineNudgePx = 1;
    const editorFontSizePx = isCompact ? 11 : 13;
    const placeholderFontSizePx = isCompact ? 11 : 13;
    const editorLineHeight = "160%";
    const PRESENTATION_EXTENSIONS = new Set([".ppt", ".pptx", ".pptm"]);
    const DOCUMENT_EXTENSIONS = new Set([".doc", ".docx"]);
    const SPREADSHEET_EXTENSIONS = new Set([".xls", ".xlsx", ".xlsm"]);
    const getNormalizedExtension = (option) => {
        const candidate = option.fileType || option.label || "";
        const trimmed = candidate.trim().toLowerCase();
        if (!trimmed)
            return "";
        if (trimmed.startsWith("."))
            return trimmed;
        const dotIndex = trimmed.lastIndexOf(".");
        return dotIndex !== -1 ? trimmed.slice(dotIndex) : "";
    };
    const getMentionVisualFamily = (option) => {
        if (option.category === "presentation")
            return "presentation";
        if (option.category === "cellRange")
            return "cellRange";
        if (option.category === "chart")
            return "chart";
        if (option.category === "sheet")
            return "sheet";
        const extension = getNormalizedExtension(option);
        if (option.source === "ppt" || PRESENTATION_EXTENSIONS.has(extension)) {
            return "presentation";
        }
        if (SPREADSHEET_EXTENSIONS.has(extension)) {
            return "spreadsheet";
        }
        if (DOCUMENT_EXTENSIONS.has(extension)) {
            return "document";
        }
        return "fallback";
    };
    const getMentionPalette = (family) => {
        switch (family) {
            case "presentation":
                return {
                    backgroundColor: "#FFF7ED",
                    borderColor: "#FDBA74",
                    textColor: "#9A3412",
                    iconColor: "#EA580C",
                };
            case "document":
                return {
                    backgroundColor: "#EFF6FF",
                    borderColor: "#93C5FD",
                    textColor: "#1D4ED8",
                    iconColor: "#2563EB",
                };
            case "spreadsheet":
                return {
                    backgroundColor: "#ECFDF5",
                    borderColor: "#86EFAC",
                    textColor: "#166534",
                    iconColor: "#16A34A",
                };
            case "cellRange":
                return {
                    backgroundColor: "#ECFDF5",
                    borderColor: "#86EFAC",
                    textColor: "#166534",
                    iconColor: "#16A34A",
                };
            case "chart":
                return {
                    backgroundColor: "#EEF2FF",
                    borderColor: "#A5B4FC",
                    textColor: "#3730A3",
                    iconColor: "#4F46E5",
                };
            case "sheet":
                return {
                    backgroundColor: "#F0FDFA",
                    borderColor: "#5EEAD4",
                    textColor: "#0F766E",
                    iconColor: "#0D9488",
                };
            default:
                return {
                    backgroundColor: "#FFFBEB",
                    borderColor: "#FCD34D",
                    textColor: "#92400E",
                    iconColor: "#ECAF00",
                };
        }
    };
    const getMentionIconMarkup = (family, color, iconSizePx) => {
        if (family === "cellRange") {
            return `<svg width="${iconSizePx}" height="${iconSizePx}" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="1.5" stroke="${color}" stroke-width="1.2"/><path d="M2 6H14M2 10H14M6 2V14M10 2V14" stroke="${color}" stroke-width="1.2"/></svg>`;
        }
        if (family === "chart") {
            return `<svg width="${iconSizePx}" height="${iconSizePx}" viewBox="0 0 16 16" fill="none"><path d="M2.5 13.5H13.5" stroke="${color}" stroke-width="1.2" stroke-linecap="round"/><rect x="3.5" y="8.5" width="2.2" height="4" rx="0.6" fill="${color}"/><rect x="6.9" y="5.5" width="2.2" height="7" rx="0.6" fill="${color}"/><rect x="10.3" y="3.5" width="2.2" height="9" rx="0.6" fill="${color}"/></svg>`;
        }
        if (family === "sheet") {
            return `<svg width="${iconSizePx}" height="${iconSizePx}" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="1.5" stroke="${color}" stroke-width="1.2"/><path d="M2 5H14" stroke="${color}" stroke-width="1.2"/><path d="M5.5 5V14M10.5 5V14" stroke="${color}" stroke-width="1.2"/></svg>`;
        }
        const IconComponent = family === "presentation"
            ? FilePpt
            : family === "spreadsheet"
                ? FileXls
                : family === "document"
                    ? FileDoc
                    : FilePdf;
        return renderToStaticMarkup(_jsx(IconComponent, { size: iconSizePx, color: color, weight: "regular" }));
    };
    const shouldShowUploadProgress = (option) => {
        if (!option.uploadId)
            return false;
        return option.uploadStatus !== "done" && option.uploadStatus !== "hiding";
    };
    const formatProgressValue = (progress) => {
        if (typeof progress !== "number" || Number.isNaN(progress))
            return 0;
        return Math.max(0, Math.min(100, Math.round(progress)));
    };
    const createMentionTagNode = (option) => {
        const mentionSpan = document.createElement("span");
        mentionSpan.contentEditable = "false";
        mentionSpan.className = "mention-tag";
        mentionSpan.setAttribute("data-mention-id", option.id);
        mentionSpan.setAttribute("data-mention-type", option.type);
        mentionSpan.setAttribute("data-mention-value", option.value);
        mentionSpan.setAttribute("data-mention-source", option.source || "external");
        mentionSpan.setAttribute("data-mention-label", option.label);
        if (option.category) {
            mentionSpan.setAttribute("data-mention-category", option.category);
        }
        if (option.fileType) {
            mentionSpan.setAttribute("data-mention-filetype", option.fileType);
        }
        if (option.metadata) {
            mentionSpan.setAttribute("data-mention-metadata", JSON.stringify(option.metadata));
        }
        if (option.uploadId) {
            mentionSpan.setAttribute("data-upload-id", option.uploadId);
        }
        const family = getMentionVisualFamily(option);
        const palette = getMentionPalette(family);
        mentionSpan.style.cssText = `
      display: inline-flex;
      align-items: center;
      gap: ${isCompact ? 2 : 3}px;
      padding: 0 ${isCompact ? 5 : 6}px;
      margin: 0 2px;
      background-color: ${palette.backgroundColor};
      border-radius: 4px;
      box-shadow: inset 0 0 0 1px ${palette.borderColor};
      color: ${palette.textColor};
      font-family: Mulish, sans-serif;
      font-size: ${mentionTagFontSizePx}px;
      font-weight: 700;
      line-height: inherit;
      vertical-align: baseline;
      position: relative;
      top: ${mentionTagBaselineNudgePx}px;
      cursor: pointer;
    `;
        const iconWrapper = document.createElement("span");
        iconWrapper.className = "mention-tag-icon";
        iconWrapper.style.cssText = `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: ${mentionTagIconSizePx}px;
      height: ${mentionTagIconSizePx}px;
      flex-shrink: 0;
      color: ${palette.iconColor};
    `;
        iconWrapper.innerHTML = getMentionIconMarkup(family, palette.iconColor, mentionTagIconSizePx);
        mentionSpan.appendChild(iconWrapper);
        const labelNode = document.createElement("span");
        labelNode.className = "mention-tag-label";
        labelNode.textContent = option.label;
        labelNode.style.color = palette.textColor;
        mentionSpan.appendChild(labelNode);
        if (shouldShowUploadProgress(option)) {
            const progressNode = document.createElement("span");
            progressNode.className = "mention-tag-progress";
            progressNode.textContent = `(${formatProgressValue(option.uploadProgress)}%)`;
            progressNode.style.fontWeight = "400";
            progressNode.style.color = palette.textColor;
            mentionSpan.appendChild(progressNode);
        }
        return mentionSpan;
    };
    const visibleMentionSections = useMemo(() => {
        if (activeSectionId) {
            const focusedSection = mentionSections.find((section) => section.id === activeSectionId);
            if (!focusedSection) {
                return [];
            }
            return [
                {
                    ...focusedSection,
                    items: focusedSection.items.slice(0, SECTION_VIEW_VISIBLE_ITEMS),
                },
            ];
        }
        return mentionSections.map((section) => ({
            ...section,
            items: section.items.slice(0, OVERVIEW_VISIBLE_ITEMS),
        }));
    }, [activeSectionId, mentionSections]);
    const displayMentionSections = useMemo(() => {
        if (mentionMenuPlacement === "above") {
            return visibleMentionSections;
        }
        // Keep visual gravity close to the active input when menu is below.
        return [...visibleMentionSections].reverse().map((section) => ({
            ...section,
            items: [...section.items].reverse(),
        }));
    }, [mentionMenuPlacement, visibleMentionSections]);
    const contextActionByCategory = useMemo(() => {
        const actionMap = new Map();
        mentionContextActions.forEach((action) => {
            if (!action.category) {
                return;
            }
            if (!actionMap.has(action.category)) {
                actionMap.set(action.category, action);
            }
        });
        return actionMap;
    }, [mentionContextActions]);
    const menuEntries = useMemo(() => {
        const entries = [];
        displayMentionSections.forEach((section) => {
            if (activeSectionId) {
                entries.push({
                    key: getBackEntryKey(section.id),
                    type: "back",
                    sectionId: section.id,
                });
            }
            else if (section.totalCount > section.items.length) {
                entries.push({
                    key: getSeeMoreEntryKey(section.id),
                    type: "seeMore",
                    sectionId: section.id,
                });
            }
            section.items.forEach((item) => {
                entries.push({
                    key: getItemEntryKey(section.id, item.id),
                    type: "item",
                    sectionId: section.id,
                    item,
                });
            });
            const contextAction = contextActionByCategory.get(section.category);
            if (contextAction) {
                entries.push({
                    key: getContextActionEntryKey(section.id, contextAction.id),
                    type: "contextAction",
                    sectionId: section.id,
                    item: contextAction,
                });
            }
            if (section.category === "file" &&
                typeof onUploadClick === "function") {
                entries.push({
                    key: getUploadEntryKey(section.id),
                    type: "upload",
                    sectionId: section.id,
                });
            }
        });
        return entries;
    }, [
        activeSectionId,
        contextActionByCategory,
        displayMentionSections,
        onUploadClick,
    ]);
    const menuEntryIndexByKey = useMemo(() => {
        const indexMap = new Map();
        menuEntries.forEach((entry, index) => {
            indexMap.set(entry.key, index);
        });
        return indexMap;
    }, [menuEntries]);
    const selectedMenuEntryKey = menuEntries[selectedMenuEntryIndex]?.key || null;
    const closeMentionMenu = useCallback(() => {
        setShowMentionMenu(false);
        setActiveSectionId(null);
        setSelectedMenuEntryIndex(0);
        setMentionContextActions([]);
    }, []);
    const handleSeeMore = (sectionId) => {
        setActiveSectionId(sectionId);
        setSelectedMenuEntryIndex(getSelectionSeedForPlacement(mentionMenuPlacement));
    };
    const handleBackToOverview = useCallback(() => {
        setActiveSectionId(null);
        setSelectedMenuEntryIndex(getSelectionSeedForPlacement(mentionMenuPlacement));
    }, [mentionMenuPlacement]);
    const handleMenuEntryHover = useCallback((entryKey) => {
        const nextIndex = menuEntryIndexByKey.get(entryKey);
        if (typeof nextIndex === "number") {
            setSelectedMenuEntryIndex(nextIndex);
        }
    }, [menuEntryIndexByKey]);
    useEffect(() => {
        if (menuEntries.length === 0) {
            if (selectedMenuEntryIndex !== 0) {
                setSelectedMenuEntryIndex(0);
            }
            return;
        }
        if (selectedMenuEntryIndex > menuEntries.length - 1) {
            setSelectedMenuEntryIndex(menuEntries.length - 1);
        }
    }, [menuEntries.length, selectedMenuEntryIndex]);
    useEffect(() => {
        if (!activeSectionId) {
            return;
        }
        if (!mentionSections.some((section) => section.id === activeSectionId)) {
            setActiveSectionId(null);
            setSelectedMenuEntryIndex(0);
        }
    }, [activeSectionId, mentionSections]);
    useEffect(() => {
        if (!showMentionMenu) {
            setActiveSectionId(null);
            setSelectedMenuEntryIndex(0);
            return;
        }
        setSelectedMenuEntryIndex(getSelectionSeedForPlacement(mentionMenuPlacement));
    }, [mentionMenuPlacement, mentionSearchQuery, showMentionMenu]);
    // Fetch mention items when the menu is open and query changes.
    useEffect(() => {
        if (!showMentionMenu || !fetchMentionItems) {
            return undefined;
        }
        let isCancelled = false;
        let timeoutId;
        const query = mentionSearchQuery.trim();
        const runFetch = async () => {
            setIsLoadingFiles(true);
            try {
                const data = (await fetchMentionItems(query)) || EMPTY_MENTION_DATA;
                if (isCancelled)
                    return;
                setMentionSections(Array.isArray(data.sections) ? data.sections : []);
                setMentionContextActions(Array.isArray(data.contextActions) ? data.contextActions : []);
            }
            catch (error) {
                if (isCancelled)
                    return;
                console.error("[ChatInput] Error loading mention items:", error);
                setMentionSections([]);
                setMentionContextActions([]);
            }
            finally {
                if (isCancelled)
                    return;
                setIsLoadingFiles(false);
            }
        };
        // Empty query should populate immediately; typed query is debounced.
        if (!query) {
            void runFetch();
        }
        else {
            timeoutId = window.setTimeout(() => {
                void runFetch();
            }, 200);
        }
        return () => {
            isCancelled = true;
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [fetchMentionItems, mentionSearchQuery, showMentionMenu]);
    // Create the placeholder hint that appears after @ (non-editable visual element)
    const createAtPlaceholder = () => {
        const placeholder = document.createElement("span");
        placeholder.className = "at-placeholder";
        placeholder.contentEditable = "false";
        placeholder.style.cssText = `
      color: #9ca3af;
      font-family: Mulish, sans-serif;
      font-size: ${placeholderFontSizePx}px;
      font-weight: 400;
      line-height: ${editorLineHeight};
      pointer-events: none;
      user-select: none;
    `;
        placeholder.textContent = "Attach a file...";
        return placeholder;
    };
    // Remove the @ placeholder from editor
    const removeAtPlaceholder = () => {
        const editor = editorRef.current;
        if (!editor)
            return;
        const placeholder = editor.querySelector(".at-placeholder");
        if (placeholder) {
            placeholder.remove();
        }
    };
    // Handle input in the contenteditable div
    const handleInput = (e) => {
        const target = e.currentTarget;
        const text = target.textContent || "";
        onChange(text);
        // Check for @ character
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const textBeforeCursor = range.startContainer.textContent?.substring(0, range.startOffset) ||
                "";
            // Find the last @ symbol
            const lastAtIndex = textBeforeCursor.lastIndexOf("@");
            if (lastAtIndex !== -1 && lastAtIndex === textBeforeCursor.length - 1) {
                // Just typed @ - insert placeholder after cursor
                removeAtPlaceholder(); // Remove any existing placeholder
                const placeholder = createAtPlaceholder();
                range.insertNode(placeholder);
                // Move cursor before the placeholder (so user types between @ and placeholder)
                range.setStartBefore(placeholder);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
                setMentionSearchQuery("");
                setShowMentionMenu(true);
                setSelectedMenuEntryIndex(getSelectionSeedForPlacement(mentionMenuPlacement));
                setActiveSectionId(null);
                updateMenuPosition();
            }
            else if (lastAtIndex !== -1 && showMentionMenu) {
                // Continuing to type after @
                const query = textBeforeCursor.substring(lastAtIndex + 1);
                // Remove placeholder when user starts typing
                removeAtPlaceholder();
                if (query.includes(" ")) {
                    // Space typed, close menu
                    closeMentionMenu();
                }
                else {
                    setMentionSearchQuery(query);
                    setSelectedMenuEntryIndex(getSelectionSeedForPlacement(mentionMenuPlacement));
                }
            }
            else if (!textBeforeCursor.includes("@") && showMentionMenu) {
                // No @ in text before cursor, close menu
                removeAtPlaceholder();
                closeMentionMenu();
            }
        }
    };
    const insertPlainTextAtCursor = (text) => {
        const editor = editorRef.current;
        if (!editor)
            return;
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            const textNode = document.createTextNode(text);
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
        else {
            editor.textContent = (editor.textContent || "") + text;
        }
        onChange(editor.textContent || "");
    };
    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData?.getData("text/plain") || "";
        insertPlainTextAtCursor(text);
    };
    // Recompute mention menu placement and available height for this input.
    const updateMenuPosition = useCallback(() => {
        const editor = editorRef.current;
        if (!editor)
            return;
        const bounds = editor.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const edgeMargin = 12;
        const desiredMaxHeight = 320;
        const minMaxHeight = 96;
        const placementThreshold = 180;
        const spaceAbove = Math.max(0, bounds.top - edgeMargin);
        const spaceBelow = Math.max(0, viewportHeight - bounds.bottom - edgeMargin);
        const placement = spaceBelow > spaceAbove && spaceAbove < placementThreshold
            ? "below"
            : "above";
        const availableSpace = placement === "above" ? spaceAbove : spaceBelow;
        const computedMaxHeight = Math.max(minMaxHeight, Math.min(desiredMaxHeight, Math.floor(availableSpace)));
        setMentionMenuPlacement(placement);
        setMentionMenuMaxHeight(computedMaxHeight);
        setMenuPosition({
            top: 8,
            left: 0,
        });
    }, []);
    // Insert mention into editor
    const insertMentionNodeAtCursor = (mentionNode) => {
        const editor = editorRef.current;
        if (!editor)
            return;
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            range.insertNode(mentionNode);
            const trailingSpace = document.createTextNode(" ");
            mentionNode.parentNode?.insertBefore(trailingSpace, mentionNode.nextSibling);
            range.setStartAfter(trailingSpace);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            return;
        }
        editor.appendChild(mentionNode);
        editor.appendChild(document.createTextNode(" "));
    };
    const insertMention = (option) => {
        const editor = editorRef.current;
        if (!editor)
            return;
        // Remove placeholder if exists
        removeAtPlaceholder();
        const mentionSpan = createMentionTagNode(option);
        // Find @ and search query in the editor text, then replace with mention
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            insertMentionNodeAtCursor(mentionSpan);
            closeMentionMenu();
            setMentionSearchQuery("");
            onChange(editor.textContent || "");
            return;
        }
        // Find the text node containing @
        const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
        let textNode = null;
        let atIndex = -1;
        while (walker.nextNode()) {
            const node = walker.currentNode;
            const idx = node.textContent?.lastIndexOf("@") ?? -1;
            if (idx !== -1) {
                textNode = node;
                atIndex = idx;
            }
        }
        if (textNode && atIndex !== -1) {
            const fullText = textNode.textContent || "";
            // Remove @ and any search query after it (up to cursor or end)
            const beforeAt = fullText.substring(0, atIndex);
            const afterAtAndQuery = fullText.substring(atIndex + 1 + mentionSearchQuery.length);
            textNode.textContent = beforeAt;
            // Insert mention after the text node
            const newRange = document.createRange();
            newRange.setStartAfter(textNode);
            newRange.collapse(true);
            newRange.insertNode(mentionSpan);
            // Add remaining text and space after mention
            const afterNode = document.createTextNode(" " + afterAtAndQuery);
            mentionSpan.parentNode?.insertBefore(afterNode, mentionSpan.nextSibling);
            // Move cursor after the space
            newRange.setStart(afterNode, 1);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
        }
        else {
            insertMentionNodeAtCursor(mentionSpan);
        }
        closeMentionMenu();
        setMentionSearchQuery("");
        // Trigger onChange with updated content
        onChange(editor.textContent || "");
    };
    const insertMentions = (options) => {
        if (!options.length)
            return;
        options.forEach((option) => insertMention(option));
    };
    // Handle keyboard navigation in mention menu
    const handleKeyDownWithMenu = (e) => {
        if (showMentionMenu) {
            const maxIndex = menuEntries.length - 1;
            if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                e.preventDefault();
                setSelectedMenuEntryIndex((prev) => prev < maxIndex ? prev + 1 : prev);
            }
            else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                e.preventDefault();
                setSelectedMenuEntryIndex((prev) => (prev > 0 ? prev - 1 : 0));
            }
            else if (e.key === "Enter") {
                e.preventDefault();
                const selectedEntry = menuEntries[selectedMenuEntryIndex];
                if (!selectedEntry) {
                    return;
                }
                if (selectedEntry.type === "item") {
                    insertMention(selectedEntry.item);
                    return;
                }
                if (selectedEntry.type === "contextAction") {
                    insertMention(selectedEntry.item);
                    return;
                }
                if (selectedEntry.type === "seeMore") {
                    handleSeeMore(selectedEntry.sectionId);
                    return;
                }
                if (selectedEntry.type === "back") {
                    handleBackToOverview();
                    return;
                }
                if (selectedEntry.type === "upload") {
                    removeAtPlaceholder();
                    onUploadClick?.();
                    closeMentionMenu();
                }
            }
            else if (e.key === "Escape") {
                e.preventDefault();
                if (activeSectionId) {
                    handleBackToOverview();
                }
                else {
                    removeAtPlaceholder();
                    closeMentionMenu();
                }
            }
            else {
                onKeyDown(e);
            }
        }
        else {
            onKeyDown(e);
        }
    };
    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            const target = e.target;
            const isElementTarget = target instanceof Element;
            const eventPath = typeof e.composedPath === "function" ? e.composedPath() : [];
            const isInsideMenuFromPath = eventPath.some((node) => {
                return (node instanceof Element &&
                    node.classList.contains("mention-menu-container"));
            });
            // Check if click is inside the mention menu or editor.
            const isInsideMenu = isInsideMenuFromPath ||
                (isElementTarget ? target.closest(".mention-menu-container") : null);
            const isInsideEditor = isElementTarget
                ? editorRef.current?.contains(target)
                : false;
            if (!isInsideMenu && !isInsideEditor) {
                closeMentionMenu();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [closeMentionMenu]);
    // Keep placement synced while menu is open (viewport, scroll, and container changes).
    useEffect(() => {
        if (!showMentionMenu) {
            return undefined;
        }
        updateMenuPosition();
        const recomputePosition = () => updateMenuPosition();
        window.addEventListener("resize", recomputePosition);
        window.addEventListener("scroll", recomputePosition, true);
        let resizeObserver = null;
        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(() => recomputePosition());
            if (editorRef.current) {
                resizeObserver.observe(editorRef.current);
            }
            if (editorRef.current?.parentElement) {
                resizeObserver.observe(editorRef.current.parentElement);
            }
        }
        return () => {
            window.removeEventListener("resize", recomputePosition);
            window.removeEventListener("scroll", recomputePosition, true);
            resizeObserver?.disconnect();
        };
    }, [showMentionMenu, updateMenuPosition]);
    // Auto-resize effect
    useEffect(() => {
        const editor = editorRef.current;
        if (editor) {
            editor.style.height = "20px";
            const scrollHeight = editor.scrollHeight;
            editor.style.height = Math.min(scrollHeight, 150) + "px";
        }
    }, [value]);
    // Keep mention upload chips in sync with live upload progress.
    useEffect(() => {
        const editor = editorRef.current;
        if (!editor || uploadingFiles.length === 0)
            return;
        const mentionTags = Array.from(editor.querySelectorAll(".mention-tag[data-upload-id]"));
        mentionTags.forEach((tag) => {
            const uploadId = tag.getAttribute("data-upload-id");
            if (!uploadId)
                return;
            const uploadState = uploadingFiles.find((file) => file.id === uploadId);
            if (!uploadState)
                return;
            if (uploadState.filepath) {
                tag.setAttribute("data-mention-value", uploadState.filepath);
            }
            const progressNode = tag.querySelector(".mention-tag-progress");
            const showProgress = uploadState.status !== "done" && uploadState.status !== "hiding";
            if (showProgress) {
                const progressText = `(${formatProgressValue(uploadState.progress)}%)`;
                if (progressNode) {
                    progressNode.textContent = progressText;
                }
                else {
                    const mentionSource = tag.getAttribute("data-mention-source") || "external";
                    const mentionLabel = tag.getAttribute("data-mention-label") || uploadState.name;
                    const mentionFileType = tag.getAttribute("data-mention-filetype") || undefined;
                    const mentionOption = {
                        id: tag.getAttribute("data-mention-id") || `upload-${uploadId}`,
                        type: "file",
                        label: mentionLabel,
                        value: uploadState.filepath ||
                            tag.getAttribute("data-mention-value") ||
                            mentionLabel,
                        category: tag.getAttribute("data-mention-category") || "file",
                        source: mentionSource,
                        fileType: mentionFileType,
                        uploadId,
                        uploadProgress: uploadState.progress,
                        uploadStatus: uploadState.status,
                    };
                    const replacement = createMentionTagNode(mentionOption);
                    tag.replaceWith(replacement);
                }
                return;
            }
            if (progressNode) {
                progressNode.remove();
            }
        });
    }, [uploadingFiles]);
    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        clear: () => {
            if (editorRef.current) {
                editorRef.current.innerHTML = "";
                onChange("");
            }
        },
        focus: () => {
            if (editorRef.current) {
                editorRef.current.focus();
            }
        },
        insertText: (text) => {
            const editor = editorRef.current;
            if (!editor)
                return;
            // Focus the editor first
            editor.focus();
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                // Delete any selected text
                range.deleteContents();
                // Create text node and insert
                const textNode = document.createTextNode(text);
                range.insertNode(textNode);
                // Move cursor to end of inserted text
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                selection.removeAllRanges();
                selection.addRange(range);
            }
            else {
                // No selection, append to end
                editor.textContent = (editor.textContent || "") + text;
            }
            // Trigger onChange with updated content
            onChange(editor.textContent || "");
        },
        insertUploadedMentions: (files) => {
            if (!files.length) {
                return;
            }
            const mentionOptions = files.map((file) => ({
                id: `upload-mention-${file.id}`,
                type: "file",
                label: file.name,
                value: file.filepath || file.name,
                category: "file",
                source: "external",
                fileType: file.name,
                uploadId: file.id,
                uploadProgress: file.progress,
                uploadStatus: file.status,
            }));
            insertMentions(mentionOptions);
        },
        getContentWithMentions: () => {
            const editor = editorRef.current;
            if (!editor) {
                return { text: "", mentions: [], html: "" };
            }
            const mentions = [];
            let textContent = "";
            let currentIndex = 0;
            const html = editor.innerHTML;
            // Parse the contenteditable content
            const childNodes = Array.from(editor.childNodes);
            childNodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const text = node.textContent || "";
                    textContent += text;
                    currentIndex += text.length;
                }
                else if (node.nodeType === Node.ELEMENT_NODE) {
                    const element = node;
                    if (element.classList.contains("mention-tag")) {
                        const mentionId = element.getAttribute("data-mention-id") || "";
                        const mentionType = element.getAttribute("data-mention-type");
                        const mentionValue = element.getAttribute("data-mention-value") || "";
                        const mentionSource = element.getAttribute("data-mention-source");
                        const mentionCategory = element.getAttribute("data-mention-category");
                        const mentionLabel = element.getAttribute("data-mention-label") ||
                            element.textContent?.trim() ||
                            "";
                        const startIndex = currentIndex;
                        textContent += mentionLabel;
                        currentIndex += mentionLabel.length;
                        mentions.push({
                            id: mentionId,
                            type: mentionType,
                            label: mentionLabel,
                            value: mentionValue,
                            startIndex,
                            endIndex: currentIndex,
                            source: mentionSource,
                            category: mentionCategory || undefined,
                        });
                    }
                    else {
                        const text = element.textContent || "";
                        textContent += text;
                        currentIndex += text.length;
                    }
                }
            });
            return { text: textContent, mentions, html };
        },
        setContentWithMentions: (content) => {
            const editor = editorRef.current;
            if (!editor)
                return;
            const safeText = content.text || "";
            const safeMentions = Array.isArray(content.mentions)
                ? [...content.mentions]
                : [];
            const sortedMentions = safeMentions
                .filter((mention) => Number.isFinite(mention.startIndex) &&
                Number.isFinite(mention.endIndex) &&
                mention.startIndex >= 0 &&
                mention.endIndex >= mention.startIndex)
                .sort((a, b) => a.startIndex - b.startIndex);
            editor.innerHTML = "";
            let currentTextIndex = 0;
            sortedMentions.forEach((mention, index) => {
                const startIndex = Math.max(currentTextIndex, Math.min(safeText.length, Math.floor(mention.startIndex)));
                const endIndex = Math.max(startIndex, Math.min(safeText.length, Math.floor(mention.endIndex)));
                const beforeText = safeText.slice(currentTextIndex, startIndex);
                if (beforeText) {
                    editor.appendChild(document.createTextNode(beforeText));
                }
                const mentionOption = {
                    id: mention.id || `prefill-mention-${index}`,
                    type: mention.type || "file",
                    label: mention.label || safeText.slice(startIndex, endIndex),
                    value: mention.value || mention.label,
                    category: mention.category,
                    source: mention.source,
                };
                editor.appendChild(createMentionTagNode(mentionOption));
                currentTextIndex = endIndex;
            });
            if (currentTextIndex < safeText.length) {
                editor.appendChild(document.createTextNode(safeText.slice(currentTextIndex)));
            }
            closeMentionMenu();
            setMentionSearchQuery("");
            onChange(editor.textContent || "");
        },
    }), [closeMentionMenu, onChange, insertMentions]);
    return (_jsxs("div", { className: "relative w-full", children: [_jsx("div", { ref: editorRef, contentEditable: !disabled, className: "w-full bg-transparent border-none outline-none text-gray-990 overflow-y-auto empty:before:content-[attr(data-placeholder)] empty:before:text-gray-500", "data-placeholder": placeholder, onInput: handleInput, onKeyDown: handleKeyDownWithMenu, onPaste: handlePaste, onFocus: onFocus, onBlur: onBlur, suppressContentEditableWarning: true, style: {
                    minHeight: "20px",
                    maxHeight: "150px",
                    fontSize: `${editorFontSizePx}px`,
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: editorLineHeight,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-wrap",
                } }), _jsx(MentionMenu, { isOpen: showMentionMenu, position: menuPosition, sections: displayMentionSections, contextActions: mentionContextActions, isLoading: isLoadingFiles, placement: mentionMenuPlacement, maxHeight: mentionMenuMaxHeight, sizeVariant: sizeVariant, onSelect: insertMention, onClose: closeMentionMenu, onSeeMore: handleSeeMore, onBack: handleBackToOverview, activeSectionId: activeSectionId, selectedEntryKey: selectedMenuEntryKey, onEntryHover: handleMenuEntryHover, onUploadClick: onUploadClick
                    ? () => {
                        removeAtPlaceholder();
                        console.log("Upload file clicked");
                        onUploadClick?.();
                        closeMentionMenu();
                    }
                    : undefined })] }));
});
ChatInput.displayName = "ChatInput";
